// hooks/useAuthModal.js
import { useModal } from "../context/ModalContext";
import { useAuth } from "../context/AuthProvider";
import { isSignedIn, loadAuth } from "../utils/firebaseAuth";

// Most pages use this hook, but the modal (Formik, yup) is only needed once
// it opens, so it loads then - and the modal opens once it has loaded.
// It used to open at once with a spinner placeholder (~100px) that the form
// (~550px) replaced a moment later: on phones the dialog is a bottom sheet,
// so its top edge jumped up the screen ~400px. That landed more than 500ms
// after the tap, so it counted as layout shift - ~0.3 CLS from one tap on
// Like/Save/Comment for every signed-out visitor (2026-10-10 probe on voice
// and collection pages).
// Plain import() rather than next/dynamic, so that once loaded the modal
// renders complete in its first frame.
let SignInModal = null;
const loadSignInModal = () =>
  import("../components/modals/SignInModal").then((m) => {
    SignInModal = m.default;
  });

const useAuthModal = () => {
  const { user } = useAuth();
  const { openModal, closeModal } = useModal();

  const openSignInModal = (onSuccess, title) => {
    // Fetch the Firebase SDK now so the provider buttons work on first click.
    loadAuth().catch(() => {});
    // Already fetched: this resolves straight away. If the chunk can't be
    // fetched (offline, a deploy swapped it), fall back to the login page.
    loadSignInModal().then(
      () => showSignInModal(onSuccess, title),
      () => {
        window.location.href = "/login";
      },
    );
  };

  const showSignInModal = (onSuccess, title) => {
    openModal(
      "sign-in-modal",
      // Default to signup: this modal gates actions for logged-out visitors
      // on acquisition surfaces (home hero, SEO hubs), who are mostly new.
      // Returning users are one click away ("Sign in") and the Google button
      // handles both cases identically anyway.
      <SignInModal
        closeModal={() => closeModal("sign-in-modal")}
        onSuccess={onSuccess}
        initialMode="signup"
        title={title}
      />,
    );
  };

  // Returns true and does nothing else if a user is signed in. Otherwise
  // opens the sign-in modal and returns false; if `onSuccess` is provided it
  // runs after a successful sign-in, so the caller's action resumes where the
  // user left off instead of making them click again.
  // `title` (optional) replaces the modal heading, e.g. "Save your cover".
  const checkAuthAndExecute = (onSuccess, title) => {
    // Firebase's answer once it has loaded; before that, AuthProvider's
    // (cached) user, so a returning visitor isn't asked to sign in again.
    if (isSignedIn(user)) {
      return true;
    } else {
      openSignInModal(onSuccess, title);
      return false;
    }
  };

  return { checkAuthAndExecute };
};

export default useAuthModal;
